import { RefObject } from 'react'

export enum ImpressionStatus {
  /**
   * 선행조건(예: 이미지 로딩)을 달성하기 전 상태
   */
  NONE,

  /**
   * 선행조건을 만족하고 엘리먼트의 시각적 노출 역치에 도달하지 않은 상태
   */
  UNDER_VISUAL_THRESHOLD,

  /**
   * 선행조건을 만족하고 엘리먼트의 시각적 노출 역치에 도달한 상태
   * 이때부터 노출 시간을 측정하기 시작한다.
   */
  OVER_VISUAL_THRESHOLD,

  /**
   * OVER_VISUAL_THRESHOLD 상태에서 노출 시간의 역치에 도달한 상태
   * 한 번 IMPRESSED 상태가 되면 다른 상태로 변하지 않습니다.
   */
  IMPRESSED,
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface TrackerOption<Context = any> {
  /** 실시간으로 임프레션 추적 상태를 시각화할지 여부 */
  isImpressionVisible?: boolean

  /**
   * Intersection Observer 인터페이스의 읽기 전용 "root" 속성은 요소 또는 문서를 식별합니다.
   * 경계는 관찰자의 대상인 요소에 대한 뷰포트의 경계 상자로 처리됩니다.
   * root가 null이면 실제 Document 뷰포트의 경계가 사용됩니다.
   */
  root?: HTMLElement | null

  /**
   * root 주변의 여백. CSS 마진 속성과 유사한 값을 가질 수 있습니다.
   * 예: "10px 20px 30px 40px" (위, 오른쪽, 아래, 왼쪽).
   */
  rootMargin?: string

  /**
   * 시각적 노출 역치 (0에서 1까지, 예를 들어 0.5 이면 노출 역치는 50%)
   * 이 역치에 도찰하면, 노출 시간 역치를 측정하기 시작한다.
   */
  visibilityThreshold?: number

  /**
   * 노출 시간의 역치(ms)로, 시각적 노출 역치를 초과한 순간부터 측정하기 시작한다.
   * 노출 시간의 역치에 도달하는 순간, onImpressed 함수를 호출한다.
   */
  durationThreshold?: number

  /**
   * hook 사용자가 hook 내부에 전달할 맥락 정보
   * hook은 내부적으로 onImpressed 함수를 호출할 때 이 변수를 인자로 전달한다.
   */
  context: Context

  /**
   * 엘리먼트의 임프레션 상태가 IMPRESSED가 될 경우 호출할 콜백 함수
   */
  onImpressed: (args: { context: Context }) => void

  /**
   * 엘리먼트를 추적하지 않고 이미 임프레션되었다고 취급할지 여부
   */
  hasImpressed?: boolean
}

export type ImpressionTracker<E extends HTMLElement = HTMLElement> = {
  /** 임프래션 상태를 추적할 엘리먼트를 담고 있는 Ref 객체 */
  targetRef: RefObject<E>

  /** 이 함수를 호출하면 임프레션 추적을 시작합니다. */
  startTracking: () => void

  /** 이 함수를 호출하면 임프레션 추적을 중지합니다. */
  stopTracking: () => void
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type UseImpressionTracker = <E extends HTMLElement = HTMLElement, Context = any>(
  option: TrackerOption<Context>,
) => ImpressionTracker<E>

export type TrackableComponentProps = {
  parent: HTMLElement | null
}
